/**
 * MarkdownContent
 * Renders AI chat message text as formatted Markdown.
 *
 * Uses react-markdown (v6) + remark-gfm for proper GFM support
 * (tables, strikethrough, task lists, autolinks).
 * Output is wrapped in Cloudscape TextContent for correct typography.
 *
 * Security: react-markdown renders to React elements (no dangerouslySetInnerHTML).
 * Links are forced to open in a new tab with rel="noopener noreferrer".
 */

import React, { useMemo } from 'react';
import { TextContent } from '@amzn/awsui-components-react';
import ReactMarkdown from 'react-markdown';
import './MarkdownContent.scss';

// remark-gfm adds GitHub Flavored Markdown: tables, strikethrough, task lists, autolinks.
// Loaded lazily so the component still works if the package isn't installed yet.
let remarkGfm: any;
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  remarkGfm = require('remark-gfm');
  // remark-gfm v1 exports the plugin as default or as the module itself
  if (remarkGfm && remarkGfm.default) remarkGfm = remarkGfm.default;
} catch {
  remarkGfm = null;
}

interface MarkdownContentProps {
  content: string;
  className?: string;
}

/**
 * Custom renderers — keep links safe and open in a new tab.
 */
const renderers = {
  a: ({ href, children, ...props }: any) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    >
      {children}
    </a>
  ),
};

export const MarkdownContent: React.FC<MarkdownContentProps> = ({ content, className }) => {
  const plugins = useMemo(() => (remarkGfm ? [remarkGfm] : []), []);

  return (
    <TextContent>
      <div className={`genai-markdown-content${className ? ` ${className}` : ''}`}>
        <ReactMarkdown
          remarkPlugins={plugins}
          components={renderers}
          // Disallow raw HTML from the agent — render it as escaped text instead
          skipHtml={false}
          allowedElements={[
            'p', 'br', 'strong', 'em', 'del', 'code', 'pre',
            'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
            'ul', 'ol', 'li', 'input',
            'blockquote', 'hr', 'a',
            'table', 'thead', 'tbody', 'tr', 'th', 'td',
          ]}
        >
          {content || ''}
        </ReactMarkdown>
      </div>
    </TextContent>
  );
};
